import { useEffect } from 'react';
import { Route, RouteProps, Switch } from 'wouter';
import Landing from './pages/static/Landing';
import { Loader } from './components/Loader';
import LandingSapphic from './pages/static/LandingSapphic';
import LandingMen40 from './pages/static/LandingMen40';
import LandingMen2040 from './pages/static/LandingMen2040';
import LandingParisMen40 from './pages/static/LandingParisMen40';
import LandingParisMen2039 from './pages/static/LandingParisMen2039';
import { MinimalMen40, MinimalMen2039, MinimalSapphic, MinimalMen50, MinimalWomen40, MinimalCoffeeMen2039, MinimalCoffeeMen40, MinimalCoffeeSapphic, MinimalCoffeeSapphic40 } from './pages/static/LandingMinimal';
import { LandingConquest } from './pages/static/LandingConquest';
import LandingSapphicStory from './pages/static/LandingSapphicStory';
import LandingSapphicChat from './pages/static/LandingSapphicChat';
import { DocCeaseDesist, DocJurySummons, DocObituary } from './pages/static/LandingDocument';
import { CoffeeQuizMen2039 } from './pages/static/LandingCoffeeQuiz';
import TitledRoute from './components/TitledRoute';
import ParticleBackground from './components/ux/ParticleBackground';
import tracking from './lib/tracking';
import { useLandingVariant } from './lib/api/useLandingVariant';
import { TemplatePage, TEMPLATE_PREVIEWS } from './pages/static/templates/registry';


export const LANDING_ROUTES: Record<string, RouteProps['component']> = {
  '/': MainLanding,
  '/m7x3': LandingSapphic,
  '/k9r2': LandingMen40,
  '/v4t8': LandingMen2040,
  '/p3f7': LandingParisMen40,
  '/q2w5': LandingParisMen2039,
  '/qm7z': MinimalMen40,
  '/r3vk': MinimalMen2039,
  '/t8nw': MinimalSapphic,
  '/y7sw': LandingSapphicStory,
  '/v9gc': LandingSapphicChat,
  '/f4cd': DocCeaseDesist,
  '/f4js': DocJurySummons,
  '/f4ob': DocObituary,
  '/n6h2': MinimalMen50,
  '/x3p9': MinimalWomen40,
  '/c9xm': LandingConquest,
  '/w6cq': CoffeeQuizMen2039,
  '/k4fc': MinimalCoffeeMen2039,
  '/b7gc': MinimalCoffeeMen40,
  '/z2sc': MinimalCoffeeSapphic,
  '/p8sc': MinimalCoffeeSapphic40,
};

function MainLanding() {
  const variantSlug = new URLSearchParams(window.location.search).get('v');
  const { data, isLoading, error } = useLandingVariant(variantSlug);

  if (variantSlug) {
    if (isLoading) {
      return (
        <div className="h-full flex items-center justify-center">
          <Loader />
        </div>
      );
    }

    if (!error && data?.data) {
      return <TemplatePage config={data.data.config} language={data.data.language} />;
    }
  }

  return <Landing />;
}

/**
 * Path-based MCP variants: any /xxxx slug not in LANDING_ROUTES is looked up
 * in landing_variants, so MCP-created pages get clean URLs with no deploy.
 */
export function VariantCatchAll({ slug }: { slug: string }) {
  const { data, isLoading, error } = useLandingVariant(slug);

  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (!error && data?.data) {
    return (
      <div className="h-screen flex flex-col">
        <div className="flex-1">
          <TemplatePage config={data.data.config} language={data.data.language} />
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex items-center justify-center">
      <div className="text-center">
        <h1 className="text-6xl font-bold text-primary-500 mb-4">404</h1>
        <p className="text-dark-muted">Page not found</p>
      </div>
    </div>
  );
}

function TemplatePreview({ template }: { template: string }) {
  const Preview = TEMPLATE_PREVIEWS[template];
  if (!Preview) {
    return (
      <div className="h-full flex items-center justify-center text-dark-muted">
        Unknown template. Available: {Object.keys(TEMPLATE_PREVIEWS).join(', ')}
      </div>
    );
  }
  return <Preview />;
}

function LandingApp() {

  useEffect(() => {
    tracking.trackPage("Landing Page");
  }, []);

  return (
    <div className="h-screen flex flex-col">
      <ParticleBackground className="absolute inset-0" maxHeight='80vh' maxParticles={16} blurPx={120} offsetY='-20%'/>

      <div className="flex-1">
        <Switch>
          {Object.entries(LANDING_ROUTES).map(([path, component]) => (
            <TitledRoute key={path} path={path} title={'Outclose - Get closer with your community'} component={component} />
          ))}
          <Route path="/tpl/:template">
            {(params) => <TemplatePreview template={params.template} />}
          </Route>
        </Switch>
      </div>
    </div>
  );
}

export default LandingApp;
